import {Controller} from '@hotwired/stimulus'

export default class extends Controller {
  static targets = []
  static values = {}

  connect() {
    // Track immediate saves to suppress UI updates
    this.immediateSubmissions = new Set()

    // Bind all form event handlers
    this.handleFormChangeBound = this.handleFormChange.bind(this)
    this.handleFormBlurBound = this.handleFormBlur.bind(this)
    this.handleFormFocusBound = this.handleFormFocus.bind(this)

    this.element.addEventListener('change', this.handleFormChangeBound)
    this.element.addEventListener('blur', this.handleFormBlurBound, true) // capture phase
    this.element.addEventListener('focus', this.handleFormFocusBound, true) // capture phase

    // Bind event listener for Turbo frame renders
    this.turboFrameRenderHandler = this.handleTurboFrameRender.bind(this)
    document.addEventListener('turbo:before-frame-render', this.turboFrameRenderHandler)
  }

  disconnect() {
    // Clean up event listeners
    this.element.removeEventListener('change', this.handleFormChangeBound)
    this.element.removeEventListener('blur', this.handleFormBlurBound, true)
    this.element.removeEventListener('focus', this.handleFormFocusBound, true)
    document.removeEventListener('turbo:before-frame-render', this.turboFrameRenderHandler)

    // Clear any pending timeouts
    if (this.submitTimeout)
      clearTimeout(this.submitTimeout)
  }

  handleFormChange(event) {
    // Ignore empty values if flag is set via data attribute
    const ignoreEmpty = this.element.dataset.autoSaveFormIgnoreEmptyValue === 'true'
    if (ignoreEmpty && !event.target.value) {
      this.element.dataset.modified = 'false'
      return
    }

    // Mark form as modified for any change
    this.element.dataset.modified = 'true'

    // Check for React Select status changes that need immediate save
    const testId = event.target.getAttribute('test-id')
    const isStatusField = (event.target.name && event.target.name.includes('[status]'))
      || (testId && testId.includes('[status]'))

    if (isStatusField) {
      // Immediate save for status fields with UI suppression
      const frameId = this.element.closest('turbo-frame')?.id
      if (frameId)
        this.immediateSubmissions.add(frameId)

      // Give React Select time to update the hidden form field
      setTimeout(() => {
        this.element.requestSubmit()
      }, 50)
    }
  }

  handleFormBlur(event) {
    // Only handle blur for form inputs, not React Select components
    if (!this.constructor.isFormInput(event.target))
      return

    this.queueSubmit(30) // 30ms timeout like original
  }

  handleFormFocus(event) {
    // Only handle focus for form inputs, not React Select components
    if (!this.constructor.isFormInput(event.target))
      return

    this.dequeueSubmit()
  }

  static isFormInput(element) {
    return element.matches('input, textarea, select')
  }

  queueSubmit(timeout = 30) {
    this.submitTimeout = setTimeout(() => {
      if (this.element.dataset.modified === 'true')
        this.element.requestSubmit()
    }, timeout)
  }

  dequeueSubmit() {
    if (this.submitTimeout) {
      clearTimeout(this.submitTimeout)
      this.submitTimeout = null
    }
  }

  handleTurboFrameRender(event) {
    const frameId = event.target.id

    if (this.immediateSubmissions.has(frameId)) {
      // Check if user is still focused in this form
      const form = event.target.querySelector('form')
      const isStillFocused = form && (
        form.contains(document.activeElement)
        || form.querySelector(':focus')
      )

      if (isStillFocused) {
        // Suppress the DOM update but clear the modified flag
        event.preventDefault()
        form.dataset.modified = 'false'
      }

      this.immediateSubmissions.delete(frameId)
    }
  }
}
